import React, { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as R from "ramda";
import { Text, Flex } from "@unifize/sarah";

import Description from "./Description";
import Owner from "./Owner";
import Members from "./Members";
import ProcessOwners from "./ProcessOwners";
import { FormBody, Form, Template as StyledTemplate } from "./styles";
import type { AppState, UID, GroupId, Email } from "src/types";
import { getBuilderDialog, getCurrentUser } from "src/reducers";
import {
  setWorkflowBuiderAttributes,
  setWorkflowBuiderAttribute,
} from "src/actions/workflows";
import * as colors from "src/styles/constants/colors";

const superAdminBaseUrl = process.env["SUPER_ADMIN_BASE_URL"];

const Template = (): React.ReactElement => {
  const dispatch = useDispatch();

  const workflowBuilder = useSelector(({ app }: { app: AppState }) =>
    getBuilderDialog(app),
  );
  const currentUser = useSelector(({ app }: { app: AppState }) =>
    getCurrentUser(app),
  );

  const handleChange = useCallback((e: any) => {
    const { id, value } = e.target;
    dispatch(setWorkflowBuiderAttributes({ [id]: value }));
  }, []);

  const handleOwner = useCallback(
    (value: UID | undefined | null) => {
      const { members } = workflowBuilder;

      if (value) {
        dispatch(
          setWorkflowBuiderAttributes({
            owner: value,
            members: R.uniq([value, ...(members || [])]),
          }),
        );
      } else {
        dispatch(setWorkflowBuiderAttributes({ owner: value }));
      }
    },
    [workflowBuilder],
  );

  const handleMembers = useCallback(
    (member: UID | Email) => {
      const { members } = workflowBuilder;

      if (R.includes(member, members)) {
        dispatch(
          setWorkflowBuiderAttribute({
            id: "members",
            value: R.reject(R.equals(member), members),
          }),
        );
      } else {
        dispatch(
          setWorkflowBuiderAttribute({
            id: "members",
            value: [...members, member],
          }),
        );
      }
    },
    [workflowBuilder],
  );

  const handleGroups = useCallback(
    (group: GroupId): void => {
      const { groups = [] } = workflowBuilder;
      if (R.includes(group, groups)) {
        dispatch(
          setWorkflowBuiderAttribute({
            id: "groups",
            value: R.reject(R.equals(group))(groups),
          }),
        );
      } else {
        dispatch(
          setWorkflowBuiderAttribute({
            id: "groups",
            value: [...groups, group],
          }),
        );
      }
    },
    [workflowBuilder],
  );

  const handleProcessOwners = useCallback(
    (processOwner: UID) => {
      const { processOwners, privacySettings } = workflowBuilder;

      const whitelist = (privacySettings || {}).whitelist || [];

      if (R.includes(processOwner, processOwners)) {
        dispatch(
          setWorkflowBuiderAttributes({
            processOwners: R.reject(R.equals(processOwner), processOwners),
          }),
        );
      } else if (R.includes(processOwner, whitelist)) {
        dispatch(
          setWorkflowBuiderAttributes({
            processOwners: [...processOwners, processOwner],
          }),
        );
      } else {
        dispatch(
          setWorkflowBuiderAttributes({
            processOwners: [...processOwners, processOwner],
            privacySettings: {
              whitelist: [...whitelist, processOwner],
            },
          }),
        );
      }
    },
    [workflowBuilder],
  );

  const {
    description,
    owner,
    members,
    processOwners,
    privacy,
    groups = [],
    id,
    orgId,
  } = workflowBuilder;

  return (
    <StyledTemplate>
      <Form>
        <FormBody>
          <Description description={description} handleChange={handleChange} />
          <Owner owner={owner} handleOwner={handleOwner} />
          <Members
            members={members}
            groups={groups}
            handleMembers={handleMembers}
            handleGroups={handleGroups}
          />

          <ProcessOwners
            privacy={privacy}
            processOwners={processOwners}
            handleProcessOwners={handleProcessOwners}
          />

          {currentUser?.email?.endsWith("@unifize.com") &&
            superAdminBaseUrl && (
              <Flex padding="8" gap="2" items="center">
                <a
                  href={`${superAdminBaseUrl}/org/${orgId}/process/${id}/builder`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Text variant="caption" color={colors.active}>
                    Configure in super admin
                  </Text>
                </a>
                <Text variant="pill" color={colors.active}>
                  ↗
                </Text>
              </Flex>
            )}
        </FormBody>
      </Form>
    </StyledTemplate>
  );
};

export default Template;
